import { FetchResult, useMutation } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Mutations, Queries } from '@shared/api';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { toFieldJson } from '@shared/scenes/entity/util/toFieldJson';
import { API } from '@shared/types/api';
import { EditableFieldRecord } from '../../types';
import { validateFields } from './useUpdateRecurringEntity';

type UpdateMutationResult = FetchResult<API.UpdateWorkflowEntityMutation>;

interface SaveEntityFieldsHooks {
  loading: boolean;
  errors: CardEntityError[];
  saveEntity: (fields: EditableFieldRecord) => Promise<UpdateMutationResult>;
}

export function useSaveEntityFields(
  entityId: string,
  workflowTemplate?: API.WorkflowTemplateFragment,
): SaveEntityFieldsHooks {
  const [updateEntityMutation, { loading, error }] = useMutation<
    API.UpdateWorkflowEntityMutation,
    API.UpdateWorkflowEntityMutationVariables
  >(Mutations.entity.updateWorkflowEntity);

  const [errors, setErrors] = useState<CardEntityError[]>([]);

  useEffect(() => {
    if (!isNil(error)) {
      // parse remote errors
      setErrors([{ description: error.message }]);
    }
  }, [error]);

  const saveEntity = useCallback(
    (fields: EditableFieldRecord) => {
      const localErrors = validateFields(fields);
      setErrors(localErrors);
      if (!isEmpty(localErrors)) {
        return Promise.reject('Local validation');
      }

      return updateEntityMutation({
        variables: {
          input: {
            id: entityId,
            fields: toFieldJson(fields, workflowTemplate),
          },
        },
        refetchQueries: [
          { query: Queries.workflow.getWorkflowEntity, variables: { input: { id: entityId } } },
        ],
      });
    },
    [entityId, updateEntityMutation, workflowTemplate],
  );

  return {
    loading,
    errors,
    saveEntity,
  };
}
